import * as React from 'react';
import { graphql } from 'react-apollo';

import type { Maybe } from '__generated__/graphql-types';
import type {
  EnterpriseContract_EnterpriseContract,
  EnterpriseContract_InvoiceBilledContractProperties,
  EnterpriseContract_SelfServeEnterpriseContractProperties,
} from '__generated__/graphql-types-do-not-use';
import { compose, withProps } from 'recompose';

import { Link } from '@coursera/cds-core';

import thirdPartyOrganizationsHomeContractsModalQuery from 'bundles/internal-enterprise/queries/ThirdPartyOrganizationsHomeContractsModal.graphql';
import type {
  ThirdPartyOrganizationsHomeContractsModalQuery,
  ThirdPartyOrganizationsHomeContractsModalQueryVariables,
} from 'bundles/internal-enterprise/queries/__generated__/ThirdPartyOrganizationsHomeContractsModal';
import Modal from 'bundles/internal-ui/components/modal/Modal';
import Table from 'bundles/internal-ui/components/table/Table';
import Naptime from 'bundles/naptimejs';
import PaymentWalletsV1 from 'bundles/naptimejs/resources/paymentWallets.v1';

import _t from 'i18n!nls/internal-enterprise';

import 'css!./__styles__/ThirdPartyOrganizationsHomeContractsModal';

type Contract = { __typename?: 'EnterpriseContract_EnterpriseContract' } & Pick<
  EnterpriseContract_EnterpriseContract,
  | 'id'
  | 'name'
  | 'startsAt'
  | 'createdAt'
  | 'hardPausedAt'
  | 'durationInMonths'
  | 'contractState'
  | 'endsAt'
  | 'endedAt'
  | 'contractType'
  | 'billingType'
  | 'isReal'
  | 'limitType'
> & {
    properties?: Maybe<
      { __typename?: 'EnterpriseContract_ContractProperties' } & {
        invoiceBilledContractProperties?: Maybe<
          { __typename?: 'EnterpriseContract_InvoiceBilledContractProperties' } & Pick<
            EnterpriseContract_InvoiceBilledContractProperties,
            'paymentProcessorId' | 'customerId' | 'user'
          >
        >;
        selfServeProperties?: Maybe<
          { __typename?: 'EnterpriseContract_SelfServeEnterpriseContractProperties' } & Pick<
            EnterpriseContract_SelfServeEnterpriseContractProperties,
            'paymentWalletId' | 'priceMappingName'
          >
        >;
      }
    >;
  };

type PropsFromCaller = {
  organizationId: string;
  closeModal: () => void;
};

type PropsFromNaptime = {
  contracts: Contract[];
};

type PropsFromNaptime2 = {
  paymentWallets?: { id: number; userId: number }[];
};

type PropsFromMapProps = {
  contracts: (Contract & {
    userId: string | undefined;
  })[];
};

type Props = PropsFromCaller & PropsFromNaptime & PropsFromNaptime2 & PropsFromMapProps;

class ThirdPartyOrganizationsHomeContractsModal extends React.Component<Props> {
  columns = [
    {
      key: 'id',
      name: _t('Contract ID'),
      render: (field: string, row: { userId: string; contractType: string }) => {
        return row.contractType === 'SELF_SERVE' ? (
          <Link href={`/internal/payments/${row.userId}/contracts`}> {field} </Link>
        ) : (
          field
        );
      },
    },
    {
      key: 'contractType',
      name: _t('Contract Type'),
    },
    {
      key: 'startsAt',
      name: _t('Start Date'),
      type: 'timestamp',
    },
    {
      key: 'endsAt',
      name: _t('End Date'),
      type: 'timestamp',
    },
  ];

  render() {
    const { contracts, closeModal } = this.props;

    return (
      <Modal
        title={_t('Contracts')}
        onRequestClose={closeModal}
        open
        scrollable
        actions={[
          {
            text: _t('Close'),
            onClick: closeModal,
            style: 'danger',
          },
        ]}
      >
        <div className="rc_ThirdPartyOrganizationsHomeContractsModal">
          {contracts &&
            (contracts.length !== 0 ? (
              <Table columns={this.columns} rows={contracts} />
            ) : (
              <div>There are not contracts for this organization</div>
            ))}
        </div>
      </Modal>
    );
  }
}

export default compose<Props, PropsFromCaller>(
  graphql<
    PropsFromCaller,
    ThirdPartyOrganizationsHomeContractsModalQuery,
    ThirdPartyOrganizationsHomeContractsModalQueryVariables,
    { contracts: Contract[] }
  >(thirdPartyOrganizationsHomeContractsModalQuery, {
    options: ({ organizationId }) => ({
      variables: {
        thirdPartyOrganizationId: organizationId,
        includeExpired: true,
      },
      context: { clientName: 'gatewayGql' },
    }),
    props: ({ data }) => {
      return {
        contracts: data?.EnterpriseContract?.queryContractsByOrgId?.elements || [],
      };
    },
  }),
  Naptime.createContainer<PropsFromNaptime2, PropsFromNaptime>(({ contracts }) => {
    // invoice-funded contract may not have
    const paymentWalletIds = contracts.reduce((acc, item) => {
      if (item.properties?.selfServeProperties?.paymentWalletId) {
        acc.push(item.properties?.selfServeProperties?.paymentWalletId);
      }
      return acc;
    }, [] as number[]);

    if (paymentWalletIds.length > 0) {
      return {
        paymentWallets: PaymentWalletsV1.multiGet(paymentWalletIds, {
          fields: ['userId'],
        }),
      };
    } else {
      return {};
    }
  }),
  withProps<PropsFromMapProps, PropsFromNaptime & PropsFromNaptime2>(({ paymentWallets, contracts }) => {
    const mapContract = contracts.map((contract) => {
      // For invoice-funded contract, extract userId from properties
      let userId: string | undefined = contract.properties?.invoiceBilledContractProperties?.user;
      // Otherwise, lookup in paymentWalletIds to find the userId
      if (!userId) {
        const paymentWalletId = contract.properties && contract.properties.selfServeProperties?.paymentWalletId;
        const item = paymentWallets?.find(({ id }) => paymentWalletId === id);
        userId = String(item?.userId);
      }

      return {
        ...contract,
        userId,
      };
    });

    return {
      contracts: mapContract,
    };
  })
)(ThirdPartyOrganizationsHomeContractsModal);
